import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ErrorMessage {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  timestamp: Date;
  autoHide?: boolean;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  private errorsSubject = new BehaviorSubject<ErrorMessage[]>([]);
  public errors$: Observable<ErrorMessage[]> = this.errorsSubject.asObservable();

  constructor() { }

  showError(message: string, type: 'error' | 'warning' | 'info' | 'success' = 'error', autoHide: boolean = true, duration: number = 5000): void {
    const error: ErrorMessage = {
      id: this.generateId(),
      message,
      type,
      timestamp: new Date(),
      autoHide,
      duration
    };

    const currentErrors = this.errorsSubject.value;
    this.errorsSubject.next([...currentErrors, error]);

    if (autoHide) {
      setTimeout(() => {
        this.removeError(error.id);
      }, duration);
    }
  }

  removeError(id: string): void {
    const currentErrors = this.errorsSubject.value;
    const filteredErrors = currentErrors.filter(error => error.id !== id);
    this.errorsSubject.next(filteredErrors);
  }

  clearAllErrors(): void {
    this.errorsSubject.next([]);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  // Helper methods for different error types
  showErrorMessage(message: string): void {
    this.showError(message, 'error');
  }

  showWarningMessage(message: string): void {
    this.showError(message, 'warning');
  }

  showInfoMessage(message: string, duration: number = 5000): void {
    this.showError(message, 'info', true, duration);
  }

  showSuccessMessage(message: string): void {
    this.showError(message, 'success');
  }

  private extractHttpErrorMessage(error: any): string | null {
    const payload = error?.error;

    if (payload) {
      if (typeof payload === 'string') {
        const trimmed = payload.trim();
        return trimmed ? trimmed : null;
      }

      if (typeof payload?.message === 'string' && payload.message.trim()) {
        return payload.message.trim();
      }

      if (typeof payload?.error === 'string' && payload.error.trim()) {
        return payload.error.trim();
      }

      if (typeof (payload as { Error?: string })?.Error === 'string') {
        const e = (payload as { Error?: string }).Error!.trim();
        if (e) return e;
      }

      const modelStateErrors = payload?.errors;
      if (modelStateErrors) {
        if (Array.isArray(modelStateErrors)) {
          const joined = modelStateErrors
            .filter((e: any) => typeof e === 'string')
            .map((e: string) => e.trim())
            .filter(Boolean)
            .join('\n');
          return joined || null;
        }

        if (typeof modelStateErrors === 'object') {
          const values: string[] = [];
          for (const key of Object.keys(modelStateErrors)) {
            const value = (modelStateErrors as any)[key];
            if (typeof value === 'string') {
              values.push(value);
            } else if (Array.isArray(value)) {
              values.push(...value.filter((v: any) => typeof v === 'string'));
            }
          }
          const joined = values.map(v => v.trim()).filter(Boolean).join('\n');
          return joined || null;
        }
      }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }

    if (typeof error === 'string' && error.trim()) {
      return error.trim();
    }

    return null;
  }

  /**
   * Texto para exibir em tela (além do toast): mensagem conhecida ou JSON formatado do corpo.
   */
  formatHttpErrorForDisplay(error: any): string {
    const extracted = this.extractHttpErrorMessage(error);
    if (extracted) {
      return extracted;
    }
    const payload = error?.error;
    if (payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
      try {
        return JSON.stringify(payload, null, 2);
      } catch {
        /* ignore */
      }
    }
    return this.buildStatusFallbackMessage(error) || 'Ocorreu um erro inesperado.';
  }

  private buildStatusFallbackMessage(error: any): string | null {
    if (!error?.status) return null;
    switch (error.status) {
      case 400:
        return 'Bad request - Please check your input';
      case 401:
        return 'Unauthorized - Please login again';
      case 403:
        return 'Forbidden - You don\'t have permission to perform this action';
      case 404:
        return 'Not found - The requested resource was not found';
      case 500:
        return 'Internal server error - Please try again later';
      default:
        return `Error ${error.status}: ${error.statusText || 'Unknown error'}`;
    }
  }

  // Method to handle HTTP error objects
  handleHttpError(error: any, customMessage?: string): void {
    const extracted = this.extractHttpErrorMessage(error);
    const statusFallback = this.buildStatusFallbackMessage(error);
    const errorMessage = extracted || customMessage || statusFallback || 'An unexpected error occurred';

    this.showError(errorMessage, 'error');
  }
}
